import { useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { FinancialAccount } from "@common/types/financialAccount";
import { PREMIUM_FEATURES } from "@common/flatRateBilling";
import { useFinancialAccountUpdateMutation } from "@api/financialAccount.api";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { useAllowFeature } from "@hooks/useAllowFeature";
import { useViewOnlyTooltipReason } from "@hooks/permissions/useViewOnlyTooltipReason";
import { updateFinancialAccountInQueryData } from "@queries/entitiesAccounts";

const CONTINUOUS_RECONCILIATION_UPGRADE_REASON =
    "Upgrade your plan to use bank reconciliation";

export function useContinuousReconciliationToggle(
    financialAccount: FinancialAccount,
) {
    const queryClient = useQueryClient();
    const toaster = useToaster();
    const { hasAccess: hasReconciliationAccess } = useAllowFeature(
        PREMIUM_FEATURES.RECONCILIATION,
    );
    const disabledReason = useViewOnlyTooltipReason(
        hasReconciliationAccess ? "" : CONTINUOUS_RECONCILIATION_UPGRADE_REASON,
    );
    const updateAccount = useFinancialAccountUpdateMutation(financialAccount, {
        onSuccess: (updatedAccount) => {
            updateFinancialAccountInQueryData(queryClient, {
                ...financialAccount,
                continuousReconciliationEnabledAt:
                    updatedAccount.continuousReconciliationEnabledAt,
            });
            toaster.toast("Updated continuous reconciliation");
        },
    });

    const setEnabled = (isEnabled: boolean) =>
        updateAccount.mutate(
            { isContinuousReconciliationEnabled: isEnabled },
            {
                onError: (error) => {
                    if (
                        !isAxiosError(error) ||
                        !error.response?.data?.message
                    ) {
                        toaster.error(
                            "Failed to update continuous reconciliation",
                        );
                    }
                },
            },
        );

    return {
        isEnabled:
            hasReconciliationAccess &&
            financialAccount.continuousReconciliationEnabledAt !== null,
        disabledReason,
        setEnabled,
        isPending: updateAccount.isPending,
    };
}
